/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import ErrorBoundary from "./components/utils/ErrorBoundary";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { blue } from "@mui/material/colors";

/* global document, Office, module, require */
const theme = createTheme({
  palette: {
    primary: {
      main: blue[500],
    },
  },
});

const title = "Axya Outlook Add-in";

// Always-on build banner (not gated by debugLog, which is a no-op in prod) so
// support/IT can confirm which bundle a client actually loaded. If this line is
// absent from the console, the client is running a stale cached bundle.
// process.env.BUILD_ID is replaced at build time by webpack DefinePlugin with a
// string literal, so reference it directly — do NOT guard with `typeof process`,
// which is always "undefined" in the browser and would force "unknown".
// eslint-disable-next-line no-console
console.log(
  "%c[Axya Add-in] build " + (process.env.BUILD_ID || "unknown"),
  "color:#1976d2;font-weight:bold"
);

const rootElement = document.getElementById("container");
const root = createRoot(rootElement);

// --- TEMPORARY shared-mailbox diagnostics (support investigation — remove once resolved).
// Auto-logs on load so IT only needs to open F12 and read the [Axya Diag] lines —
// no manual console pasting. Also detects the case where Office.onReady never fires,
// which is the shared-mailbox failure symptom and can't be caught by manual pasting.
let officeReadyFired = false;
function logDiagnostics(info) {
  try {
    const ctx = (typeof Office !== "undefined" && Office.context) || null;
    const req = ctx && ctx.requirements;
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] onReady info:", JSON.stringify(info || null));
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] diagnostics:", JSON.stringify(ctx && ctx.diagnostics));
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] Mailbox 1.13 supported:", req ? req.isSetSupported("Mailbox", "1.13") : "n/a");
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] Mailbox 1.8 supported:", req ? req.isSetSupported("Mailbox", "1.8") : "n/a");
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] mailbox defined:", !!(ctx && ctx.mailbox));
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] item defined:", !!(ctx && ctx.mailbox && ctx.mailbox.item));
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] restUrl (EXO vs on-prem):", ctx && ctx.mailbox ? ctx.mailbox.restUrl : "n/a");
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] userProfile email:", ctx && ctx.mailbox && ctx.mailbox.userProfile ? ctx.mailbox.userProfile.emailAddress : "n/a");
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] Office.context keys:", ctx ? Object.keys(ctx).join(", ") : "(no Office.context)");
    // getSharedPropertiesAsync succeeds ONLY in a genuine shared context — i.e. it
    // proves SupportsSharedFolders is active AND we're in the shared mailbox. Its
    // targetMailbox REST URL also reveals the shared mailbox's homing (EXO vs on-prem).
    const sharedItem = ctx && ctx.mailbox && ctx.mailbox.item;
    if (sharedItem && typeof sharedItem.getSharedPropertiesAsync === "function") {
      try {
        sharedItem.getSharedPropertiesAsync(function (r) {
          if (r && r.status === "succeeded" && r.value) {
            // eslint-disable-next-line no-console
            console.log("[Axya Diag] SHARED context OK — owner:", r.value.owner, "| permissions:", r.value.delegatePermissions, "| targetMailbox:", r.value.targetMailbox);
          } else {
            // eslint-disable-next-line no-console
            console.log("[Axya Diag] getSharedPropertiesAsync: NOT a shared context (personal mailbox, or shared support inactive):", r && r.error && r.error.message);
          }
        });
      } catch (shErr) {
        // eslint-disable-next-line no-console
        console.log("[Axya Diag] getSharedPropertiesAsync threw:", shErr && shErr.message);
      }
    } else {
      // eslint-disable-next-line no-console
      console.log("[Axya Diag] getSharedPropertiesAsync: not available on this item/client");
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] error while logging:", e && e.message);
  }
}
setTimeout(function () {
  if (!officeReadyFired) {
    // eslint-disable-next-line no-console
    console.log("[Axya Diag] Office.onReady did NOT fire within 8s — host did not provide the mailbox context.");
    logDiagnostics(null);
  }
}, 8000);
// --- end diagnostics ---

/* Render application after Office initializes */
Office.onReady((info) => {
  officeReadyFired = true;
  logDiagnostics(info);
  root.render(
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <App title={title} />
      </ThemeProvider>
    </ErrorBoundary>
  );
});

if (module.hot) {
  module.hot.accept("./App", () => {
    const NextApp = require("./App").default;
    root.render(NextApp);
  });
}
